import { useState, useEffect, useMemo, useCallback } from "react"
import { useParams } from "react-router-dom"
import _ from "lodash"
import dayjs from "dayjs"
import { GTM_VIEW_FEATURE_DETAIL } from "constant"
import { useVenue } from "providers/venue"

import { sanitizeFeatureId } from "./useFeatureQuickSearch/utils"
import { getFeatureDetail, GetFeatureDetailResult } from "utils/feature"
import { pushDataLayer } from "utils/googletagmanager"
import { Id, OccupantFeature, Product, Promotion } from "providers/venue/types"
import { IConfigIcon } from "utils/icon"
import { IFeature } from "interfaces"

export type IFeatureDetailResult = GetFeatureDetailResult & {
  dataLoaded: boolean
  themeLoaded: boolean
  feature: IFeature
  categoryIconProp: IConfigIcon
  relatedOccupants: OccupantFeature[]
  promotions: Promotion[]
  products: Product[]
  isNew: boolean
  isComingSoon: boolean
}

export type UseFeatureDetail = (id?: Id) => IFeatureDetailResult

export const useFeatureDetail: UseFeatureDetail = (id) => {
  const {
    dataLoaded,
    themeLoaded,
    findFeatureById,
    filterOccupantsByName,
    mapTheme,
    promotions,
    products,
    pageConfig,
  } = useVenue()

  const [feature, setFeature] = useState<IFeature | null>(null)
  const { placeId: placeIdFromParam } = useParams()
  const placeId = id || placeIdFromParam
  const featurePromotions = useMemo<Promotion[]>(() => {
    const featureId = sanitizeFeatureId(placeId)
    return promotions.filter(
      (promotion) => promotion.properties?.feature?.id === featureId
    )
  }, [placeId, promotions])

  const featureProducts = useMemo<Product[]>(() => {
    const featureId = sanitizeFeatureId(placeId)
    return (
      products?.filter(
        (product) => product.properties?.occupant?.id === featureId
      ) || []
    )
  }, [placeId, products])

  const relatedOccupants = useMemo(() => {
    if (!feature) return []

    const otherOccupants = _.sortBy(
      filterOccupantsByName(feature.properties?.name?.en),
      ["properties.ordinal"]
    )
    return (
      otherOccupants.length > 1 ? otherOccupants : []
    ) as OccupantFeature[]
  }, [feature, filterOccupantsByName])

  useEffect(() => {
    if (!dataLoaded) return

    const feature = findFeatureById(placeId)
    setFeature(feature)
  }, [dataLoaded, placeId, findFeatureById])

  const featureDetail = useMemo(
    () => (getFeatureDetail(feature) || {}) as GetFeatureDetailResult,
    [feature]
  )

  const featureStatus = useMemo(() => {
    const currentDate = dayjs()
    const periodInDays = _.get(pageConfig, "newShop.metadata.periodInDays", 30)
    const featureType = feature?.feature_type
    const status = {
      isNew: false,
      isComingSoon: false,
    }
    // Use switch-case to handle statuses for other feature types if needed.
    switch (featureType) {
      case "occupant":
        const occupantStartDate = dayjs(feature.properties.start_date)
        status.isNew =
          currentDate.isSameOrAfter(occupantStartDate) &&
          currentDate.diff(occupantStartDate, "day") <= periodInDays
        status.isComingSoon = occupantStartDate.isAfter(currentDate)
        break
      default:
        break
    }
    return status
  }, [feature, pageConfig])

  useEffect(() => {
    try {
      if (feature) {
        const localCategoriesName = _.map(
          feature?.properties?.local_categories,
          (taxonomy) => _.get(taxonomy, "properties.name.en")
        ).join(",")
        pushDataLayer({
          event: GTM_VIEW_FEATURE_DETAIL,
          feature_id: _.get(feature, "id"),
          feature_type: _.get(feature, "feature_type"),
          feature_name: _.get(feature, "properties.name.en"),
          feature_category: _.get(feature, "properties.category"),
          local_categories: localCategoriesName,
        })
      }
    } catch (err) {
      console.log(err)
    }
  }, [feature])

  const getAmenityIconProp = useCallback(
    (amenityCategory) => {
      const iconPath = _.get(
        mapTheme,
        `${amenityCategory}.label.marker.symbol.markerPath`
      )
      const image = _.get(
        mapTheme,
        `${amenityCategory}.label.marker.symbol.markerFile`
      )
      return iconPath ? { icon_path: iconPath } : { image }
    },
    [mapTheme]
  )

  const categoryIconProp = useMemo(() => {
    if (feature && featureDetail) {
      return feature.feature_type === "amenity"
        ? getAmenityIconProp(featureDetail.category)
        : {
            icon_path: _.get(featureDetail, "categoryIconPath"),
            image: _.get(featureDetail, "categoryIconImage"),
          }
    }
  }, [getAmenityIconProp, featureDetail, feature])

  return {
    ...featureDetail,
    ...featureStatus,
    products: featureProducts,
    promotions: featurePromotions,
    feature,
    relatedOccupants, //TODO:: Create ui display button other locations
    dataLoaded,
    themeLoaded,
    categoryIconProp,
  }
}
